import { createApi } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL, createBaseQuery } from '../base/baseApi';

export const clientApi = createApi({
    tagTypes: [
        'Client',
        'MarketAnalysis',
        'BusinessModel',
        'RevenueForecast',
        'FundingPlan',
        'ExitStrategy',
        'FullPlan',
        'ReviewPlan',
        'BusinessSetupProgress',
        'ShoplineConnections',
        'QuickBooks',
    ],
    reducerPath: 'clientApi',
    baseQuery: createBaseQuery({
        baseUrl: API_BASE_URL,
        credentials: 'include', // sends cookies (CSRF)
        headers: { Accept: 'application/json' },
    }),
    endpoints: (builder) => ({
        fetchData: builder.query({
            query: () => '/financials/fetch-data',
            transformResponse: (response) => response.Data,
        }),
        refreshData: builder.query({
            query: () => '/financials/refresh-data',
            transformResponse: (response) => response.Data,
        }),
        onboarding: builder.mutation({
            query: (data) => ({
                url: '/financials/onboarding',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.Data,
        }),
        OnboardingFeedback: builder.mutation({
            query: ({SCE_ID, ...body}) => ({
                url: `/feedback/onboarding/${SCE_ID}`,
                method: 'POST',
                body,
            }),
        }),
        StepFeedback: builder.mutation({
            query: ({SCE_ID, ...body}) => ({
                url: `/feedback/step/${SCE_ID}`,
                method: 'POST',
                body,
            }),
        }),
        createTicket: builder.mutation({    
            query: (data) => ({
                url: '/support/create',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.Data,
        }),
        replyTicket: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/support/reply/${id}`,
                method: 'PUT',
                body,
            }),
            transformResponse: (response) => response.Data,
        }),
        createBussiness: builder.mutation({
            query: (data) => ({
                url: '/financials/business/create',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.Data,
        }),
        updateBussiness: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/financials/business/update/${id}`,
                method: 'PUT',
                body,
            }),
            transformResponse: (response) => response.Data,
        }),
        deleteBussiness: builder.mutation({
            query: (id) => ({
                url: `/financials/business/delete/${id}`,
                method: 'DELETE',
            }),
            transformResponse: (response) => response.Data,
        }),
        createScenario: builder.mutation({
            query: (data) => ({
                url: '/financials/scenarios/create',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.Data,
        }),
        updateScenario: builder.mutation({
            query: ({ id, ...body }) => ({
                url: `/financials/scenarios/update/${id}`,
                method: 'PUT',
                body,
            }),
            transformResponse: (response) => response.Data,
        }),
        deleteScenario: builder.mutation({
            query: (id) => ({
                url: `/financials/scenarios/delete/${id}`,
                method: 'DELETE',
            }),
            transformResponse: (response) => response.Data,
        }),
        matchCollaborators: builder.query({
            query: ({ USR }) => ({
                url: '/financials/collaborators/match',
                params: { USR },
            }),
            transformResponse: (response) => response.Data,
        }),
        addCollaborator: builder.mutation({
            query: ({ ID, ...data }) => ({
                url: `/financials/collaborators/create/${ID}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response) => response.Data,
        }),
        removeCollaborator: builder.mutation({
            query: ({ ID, ...data }) => ({
                url: `/financials/collaborators/invoke/${ID}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response) => response.Data,
        }),
        InvokeCollaboration: builder.mutation({
            query: (ID) => ({
                url: `/financials/collaborators/invoke-self/${ID}`,
                method: 'PUT',
            }),
            transformResponse: (response) => response.Data,
        }),
        AcceptInvitation: builder.mutation({
            query: (ID) => ({
                url: `/financials/collaborators/accept/${ID}`,
                method: 'PUT',
            }),
            transformResponse: (response) => response.Data,
        }),
        RejectInvitation: builder.mutation({
            query: (ID) => ({
                url: `/financials/collaborators/reject/${ID}`,
                method: 'PUT',
            }),
            transformResponse: (response) => response.Data,
        }),
        FetchQuestionner: builder.query({
            query: ({ questionner, BUS_ID, SCE_ID }) => ({
                url: `/Questionner/fetch/${questionner}`,
                params: { BUS_ID, SCE_ID },
            }),
            transformResponse: (response) => response.Data,
        }),
        QuestionnerActivity: builder.mutation({
            query: ({ title, ...body }) => ({
                url: `/Questionner/activity/${title}`,
                method: 'POST',
                body,
            }),
            transformResponse: (response) => response.Data,
        }),
        FetchQuestionnerResponse: builder.query({
            query: ({ Step, BUS_ID, SCE_ID, QuestionId }) => ({
                url: `/Questionner/fetch-response/${Step}`,
                params: { BUS_ID, SCE_ID, QuestionId },
            }),
            transformResponse: (response) => response.Data,
        }),
        StoreResponses: builder.mutation({
            query: ({ step, ...body }) => ({
                url: `/Questionner/store/${step}`,
                method: 'POST',
                body,
            }),
            transformResponse: (response) => response.Data,
        }),
        SubmitQuestionner: builder.mutation({
            query: ({ questionner, ...data }) => ({
                url: `/Questionner/submit/${questionner}`,
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.Data,
        }),
        ExportReport: builder.query({
            query: ({ BUS_ID, SCE_ID }) => ({
                url: `/reports/export`,
                params: { BUS_ID, SCE_ID },
            }),
            transformResponse: (response) => response.Data,
        }),
        // Business Setup API endpoints
        // Market Analysis - Optimized caching
        fetchMarketAnalysis: builder.query({
            query: () => '/business-setup/market-analysis',
            transformResponse: (response) => response.data,
            providesTags: ['MarketAnalysis'],
            keepUnusedDataFor: 60, // 1 minute cache
        }),
        createMarketAnalysis: builder.mutation({
            query: (data) => ({
                url: '/business-setup/market-analysis',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['MarketAnalysis'],
        }),
        updateMarketAnalysis: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `/business-setup/market-analysis/${id}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['MarketAnalysis'],
        }),
        // Business Model - Optimized caching
        fetchBusinessModel: builder.query({
            query: () => '/business-setup/business-model',
            transformResponse: (response) => response.data,
            providesTags: ['BusinessModel'],
            keepUnusedDataFor: 60, // 1 minute cache
        }),
        createBusinessModel: builder.mutation({
            query: (data) => ({
                url: '/business-setup/business-model',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['BusinessModel'],
        }),
        updateBusinessModel: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `/business-setup/business-model/${id}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['BusinessModel'],
        }),
        // Revenue Forecast - Optimized caching
        fetchRevenueForecast: builder.query({
            query: () => '/business-setup/revenue-forecast',
            transformResponse: (response) => response.data,
            providesTags: ['RevenueForecast'],
            keepUnusedDataFor: 60, // 1 minute cache
        }),
        createRevenueForecast: builder.mutation({
            query: (data) => ({
                url: '/business-setup/revenue-forecast',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['RevenueForecast'],
        }),
        updateRevenueForecast: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `/business-setup/revenue-forecast/${id}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['RevenueForecast'],
        }),
        // Funding Plan - Optimized caching
        fetchFundingPlan: builder.query({
            query: () => '/business-setup/funding-plan',
            transformResponse: (response) => response.data,
            providesTags: ['FundingPlan'],
            keepUnusedDataFor: 60, // 1 minute cache
        }),
        createFundingPlan: builder.mutation({
            query: (data) => ({
                url: '/business-setup/funding-plan',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['FundingPlan'],
        }),
        updateFundingPlan: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `/business-setup/funding-plan/${id}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['FundingPlan'],
        }),
        // Exit Strategy - Optimized caching
        fetchExitStrategy: builder.query({
            query: () => '/business-setup/exit-strategy',
            transformResponse: (response) => response.data,
            providesTags: ['ExitStrategy'],
            keepUnusedDataFor: 60, // 1 minute cache
        }),
        createExitStrategy: builder.mutation({
            query: (data) => ({
                url: '/business-setup/exit-strategy',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['ExitStrategy'],
        }),
        updateExitStrategy: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `/business-setup/exit-strategy/${id}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['ExitStrategy'],
        }),
        // Full Plan - Optimized caching
        fetchFullPlan: builder.query({
            query: () => '/business-setup/full-plan',
            transformResponse: (response) => response.data,
            providesTags: ['FullPlan'],
            keepUnusedDataFor: 60, // 1 minute cache
        }),
        createFullPlan: builder.mutation({
            query: (data) => ({
                url: '/business-setup/full-plan',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['FullPlan'],
        }),
        updateFullPlan: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `/business-setup/full-plan/${id}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['FullPlan'],
        }),
        // Review Plan - Optimized caching
        fetchReviewPlan: builder.query({
            query: () => '/business-setup/review-plan',
            transformResponse: (response) => response.data,
            providesTags: ['ReviewPlan'],
            keepUnusedDataFor: 60, // 1 minute cache
        }),
        createReviewPlan: builder.mutation({
            query: (data) => ({
                url: '/business-setup/review-plan',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['ReviewPlan'],
        }),
        updateReviewPlan: builder.mutation({
            query: ({ id, ...data }) => ({
                url: `/business-setup/review-plan/${id}`,
                method: 'PUT',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['ReviewPlan'],
        }),

        // Business Setup Progress endpoint with optimized caching
        getBusinessSetupProgress: builder.query({
            query: () => '/business-setup/progress',
            transformResponse: (response) => response.data,
            providesTags: ['BusinessSetupProgress'],
            // Reduced cache time for better responsiveness (30 seconds)
            keepUnusedDataFor: 30,
        }),

        // Unified Business Setup Data endpoint - loads ALL data at once
        getAllBusinessSetupData: builder.query({
            query: () => '/business-setup/all-data',
            transformResponse: (response) => response.data,
            providesTags: [
                'MarketAnalysis', 'BusinessModel', 'RevenueForecast',
                'FundingPlan', 'ExitStrategy', 'FullPlan', 'ReviewPlan'
            ],
            keepUnusedDataFor: 60, // 1 minute cache
        }),
        // Commented out detailed progress to avoid 404
        // getDetailedBusinessSetupProgress: builder.query({
        //     query: () => '/business-setup/progress/detailed',
        //     transformResponse: (response) => response.data,
        //     providesTags: ['BusinessSetupProgress'],
        //     keepUnusedDataFor: 45,
        // }),

        // Shopline Integration Endpoints
        shoplineInstall: builder.mutation({
            query: (storeHandle) => ({
                url: '/shopline/install',
                method: 'GET',
                params: { store_handle: storeHandle },
            }),
            transformResponse: (response) => response,
        }),
        getShoplineConnections: builder.query({
            query: () => '/shopline/connections',
            transformResponse: (response) => response,
            providesTags: ['ShoplineConnections'],
        }),
        disconnectShopline: builder.mutation({
            query: (connectionId) => ({
                url: `/shopline/disconnect/${connectionId}`,
                method: 'DELETE',
            }),
            transformResponse: (response) => response.Data,
            invalidatesTags: ['ShoplineConnections'],
        }),

        // Shopline Analytics Endpoints
        getShoplineRevenue: builder.query({
            query: (params) => ({
                url: '/shopline/analytics/revenue',
                params,
            }),
            transformResponse: (response) => response.data,
            providesTags: ['ShoplineAnalytics'],
        }),
        getShoplineCAC: builder.query({
            query: (params) => ({
                url: '/shopline/analytics/cac',
                params,
            }),
            transformResponse: (response) => response.data,
            providesTags: ['ShoplineAnalytics'],
        }),
        getShoplineLTV: builder.query({
            query: (params) => ({
                url: '/shopline/analytics/ltv',
                params,
            }),
            transformResponse: (response) => response.data,
            providesTags: ['ShoplineAnalytics'],
        }),
        getShoplineDashboard: builder.query({
            query: (params) => ({
                url: '/shopline/analytics/dashboard',
                params,
            }),
            transformResponse: (response) => response.data,
            providesTags: ['ShoplineAnalytics'],
        }),
        addShoplineAdSpend: builder.mutation({
            query: (data) => ({
                url: '/shopline/analytics/ad-spend',
                method: 'POST',
                body: data,
            }),
            transformResponse: (response) => response.data,
            invalidatesTags: ['ShoplineAnalytics'],
        }),
        syncSession: builder.mutation({
            query: () => ({
                url: '/sync-session',
                method: 'POST',
            }),
        }),

        // QuickBooks Integration Endpoints
        getQuickBooksStatus: builder.query({
            query: () => '/financials/integrations/quickbooks/status',
            transformResponse: (response) => response,
            providesTags: ['QuickBooks'],
            // Cache for 5 minutes to reduce unnecessary calls
            keepUnusedDataFor: 300,
        }),
        quickBooksConnect: builder.mutation({
            query: () => ({
                url: '/financials/integrations/quickbooks/connect',
                method: 'POST',
            }),
            transformResponse: (response) => response,
        }),
        quickBooksSync: builder.mutation({
            query: () => ({
                url: '/financials/integrations/quickbooks/sync',
                method: 'POST',
            }),
            transformResponse: (response) => response,
            invalidatesTags: ['QuickBooks'],
        }),
        quickBooksDisconnect: builder.mutation({
            query: () => ({
                url: '/financials/integrations/quickbooks/disconnect',
                method: 'POST',
            }),
            transformResponse: (response) => response,
            invalidatesTags: ['QuickBooks'],
        }),
        getQuickBooksDashboard: builder.query({
            query: () => '/reports/quickbooks-dashboard',
            transformResponse: (response) => response,
            providesTags: ['QuickBooks'],
        }),
    }),
});

export const {
    useFetchDataQuery,
    useLazyRefreshDataQuery,
    useOnboardingMutation,
    useOnboardingFeedbackMutation,
    useStepFeedbackMutation,
    useCreateTicketMutation,
    useReplyTicketMutation,
    useCreateBussinessMutation,
    useUpdateBussinessMutation,
    useDeleteBussinessMutation,
    useCreateScenarioMutation,
    useUpdateScenarioMutation,
    useDeleteScenarioMutation,
    useMatchCollaboratorsQuery,
    useAddCollaboratorMutation,
    useRemoveCollaboratorMutation,
    useInvokeCollaborationMutation,
    useAcceptInvitationMutation,
    useRejectInvitationMutation,
    useFetchQuestionnerQuery,
    useQuestionnerActivityMutation,
    useStoreResponsesMutation,
    useSubmitQuestionnerMutation,
    useLazyFetchQuestionnerResponseQuery,
    useLazyExportReportQuery,
    // Business Setup hooks
    useFetchMarketAnalysisQuery,
    useCreateMarketAnalysisMutation,
    useUpdateMarketAnalysisMutation,
    useFetchBusinessModelQuery,
    useCreateBusinessModelMutation,
    useUpdateBusinessModelMutation,
    useFetchRevenueForecastQuery,
    useCreateRevenueForecastMutation,
    useUpdateRevenueForecastMutation,
    useFetchFundingPlanQuery,
    useCreateFundingPlanMutation,
    useUpdateFundingPlanMutation,
    useFetchExitStrategyQuery,
    useCreateExitStrategyMutation,
    useUpdateExitStrategyMutation,
    useFetchFullPlanQuery,
    useCreateFullPlanMutation,
    useUpdateFullPlanMutation,
    useFetchReviewPlanQuery,
    useCreateReviewPlanMutation,
    useUpdateReviewPlanMutation,
    useGetBusinessSetupProgressQuery,
    useGetAllBusinessSetupDataQuery,
    // useGetDetailedBusinessSetupProgressQuery, // Commented out to avoid 404

    // Shopline Integration hooks
    useShoplineInstallMutation,
    useGetShoplineConnectionsQuery,
    useDisconnectShoplineMutation,
    useSyncSessionMutation,

    // Shopline Analytics hooks
    useGetShoplineRevenueQuery,
    useGetShoplineCACQuery,
    useGetShoplineLTVQuery,
    useGetShoplineDashboardQuery,
    useAddShoplineAdSpendMutation,

    // QuickBooks hooks
    useGetQuickBooksStatusQuery,
    useQuickBooksConnectMutation,
    useQuickBooksSyncMutation,
    useQuickBooksDisconnectMutation,
    useGetQuickBooksDashboardQuery,
} = clientApi;